'use client';

import React, { useCallback } from 'react';
import { ImageLoading } from '@pixum/logo';
import clsx from 'clsx';
import {
  CONTENTFUL_CARD_TYPES,
  CONTENTFUL_MOBILE_PRESENTATION_TYPES,
  LINK_TYPES,
  PRODUCT_ATTRIBUTE_STATUS_VALUES
} from 'data-layer/constants';
import type { Constant, Device, LinkNode } from 'data-layer';
import { nth, isLinkType } from 'utils';
import { buildIconAddonsFromFeatureFlag } from '../../../utils/icon/buildIconAddonsFromFeatureFlag';
import { Image, IMAGE_TRIM } from '../../image';
import { RichTextBody } from '../../richtText';
import type { CardOnClickProps, CardVariantProps } from '../types';
import { CardWithLinkMasking } from './NavigationCard';
import { ButtonWithAddToCart } from '../../buttonCollection/variants/ButtonWithAddToCart';
import { ButtonVariants, ButtonSizes } from '@pixum/button';
import { useDialogStore } from 'stores/dialog';
import { isRichTextEmpty } from '../../richtText/utils/isRichTextEmpty';

/**
 * Generates the primary action configuration for the product card.
 * Returns a button component for add-to-cart links, or text action for regular links.
 *
 * @param link - The primary link node with action details
 * @param device - Current device type (mobile/desktop)
 * @param constants - Application constants (optional, passed through to ButtonWithAddToCart)
 * @param masked - Whether the card link is masked (JS-only navigation); omits href to prevent CardComponent from using it as a headline anchor fallback
 * @returns Action object with either children (button) or text
 */
function getPrimaryActionObject(link: LinkNode | undefined, device: Device, constants: Constant | undefined, masked = false) {
  // If it's an add-to-cart link, return a ButtonWithAddToCart component as children
  if (isLinkType(link, LINK_TYPES.ADD_TO_CART)) {
    return {
      children: (
        <ButtonWithAddToCart
          link={link}
          device={device}
          variant={ButtonVariants.TERTIARY}
          size={ButtonSizes.MEDIUM}
          constants={constants}
        />
      )
    };
  }

  // Masked links (e.g. editor/online-designer) must not expose href here.
  // CardComponent falls back to primaryAction.href when its own href prop is absent,
  // which would cause the headline h3 to render an <a> tag — bypassing withLinkMasking.
  // Navigation for masked cards is handled via the JS click handler instead.
  return {
    text: link?.title ?? '',
    ...(masked ? {} : { href: link?.url ?? '' })
  };
}

/*
 * The ProductCard component is responsible for rendering a navigation card
 * with a headline, body, media, and a primary call-to-action (CTA) link.
 *
 * Props:
 * - card: An object containing the card data, including id, headline, CTA, images, and text.
 * - isMobile: A boolean indicating whether the component is being rendered in a mobile context.
 *
 * Key Features:
 * - Dynamically selects the appropriate image (desktop or mobile) based on the isMobile prop.
 * - Uses the RichTextBody component to render the card's text content.
 * - Constructs a media element using the Image component with lazy loading.
 * - Generates a Card component with various props such as body, href, id, media, and headline.
 *
 * Notes:
 * - The href is derived from the primary CTA's URL or defaults to an empty string if not provided.
 * - The textAlignment is hardcoded to "left" for consistent alignment across cards.
 */
export function ProductCard(props: CardVariantProps) {
  const {
    id,
    headline,
    subHeadline,
    overline,
    links,
    image,
    wrapperTag,
    text,
    className,
    featureFlag,
    device,
    chips,
    constants,
    productData,
    supportingText,
    layout
  } = props;
  const openDialog = useDialogStore(state => state.openDialog);
  const link = nth(links, 0, {} as LinkNode);
  const href = link?.url || '';
  const masked = link?.masked || false;
  const mediaAddons = buildIconAddonsFromFeatureFlag(featureFlag);
  const media = <Image image={image} loading={ImageLoading.Lazy} device={device} trim={IMAGE_TRIM.NONE} />;

  const body = !isRichTextEmpty(text) ? <RichTextBody text={text} /> : undefined;

  // Products without productData are considered active by default. Content-creator apply product cards to Editor and
  // Configurator Links, and sometimes they don't need to add product id
  const isProductActive = productData ? productData.status === PRODUCT_ATTRIBUTE_STATUS_VALUES.ACTIVE : true;
  const isCardClickable = isProductActive && !isLinkType(link, LINK_TYPES.ADD_TO_CART);
  const primaryAction = isProductActive ? getPrimaryActionObject(link, device, constants, masked) : undefined;
  // Cards with text hyperlinks keep their href so the card hover state is preserved.
  // wrapClickHandler in withLinkMasking intercepts clicks on text links inside .card__body
  // and lets them navigate independently without triggering card navigation.
  const cardHref = isProductActive && !isLinkType(link, LINK_TYPES.ADD_TO_CART) ? href : undefined;

  const handleOnClick = useCallback(
    (e: CardOnClickProps) => {
      if (!link || !isLinkType(link, LINK_TYPES.ORDER_WAYS_PRODUCT)) {
        return;
      }

      e?.preventDefault();
      e?.stopPropagation();

      openDialog({
        type: link.id || 'order-ways-product',
        url: link.url
      });
    },
    [link, productData]
  );

  return (
    <CardWithLinkMasking
      as={wrapperTag}
      className={clsx('product-card', className)}
      body={body}
      clickable={isCardClickable}
      href={cardHref}
      id={id}
      chips={chips}
      media={media}
      variant={CONTENTFUL_CARD_TYPES.PRODUCT}
      headline={headline}
      subheadline={subHeadline}
      overline={overline}
      textAlignment="left"
      mediaAddons={mediaAddons}
      masked={masked}
      onClick={handleOnClick}
      primaryAction={primaryAction}
      supportingText={supportingText}
      isMiniCard={layout === CONTENTFUL_MOBILE_PRESENTATION_TYPES.TWO_COLUMN_GRID}
    />
  );
}
